import { Button } from "components/Button/Button";
import {
    Autocomplete,
    AutocompleteOptionsProps,
} from "components/Form/components/Autocomplete/Autocomplete";
import {
    FormComponentPropLanguages,
    FormComponentProps,
} from "components/Form/components/FormComponentProps";
import { LazyResizeableCodeEditor } from "components/Form/lazyCodeEditorComponents";
import { Number as NumberInput } from "components/Form/components/Number/Number";
import { SimpleToggle } from "components/Form/components/Switch/Switch";
import { Text } from "components/Form/components/Text/Text";
import { GenericDateTime } from "components/Form/components/GenericDateTime/GenericDateTime";
import { IconCollection } from "components/Icon/IconCollection";
import Tooltip from "components/Tooltip/Tooltip";
import {
    AllowedValueRow,
    normalizeAllowedValuesRows,
} from "config/resources/objectTemplateAttributeAllowedValues";
import {
    controlKindForDatatype,
    getInvalidDraftRaw,
    isInvalidDraft,
    isUnsetDefaultValue,
    parseJsonDraft,
    serializeJsonDraft,
} from "config/resources/objectTemplateAttributeDefaultValue";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useFormState } from "react-final-form";
import "./DefaultValueEditor.scss";

/**
 * Datatype-aware editor for an OBJECT_TEMPLATE_ATTRIBUTE `default_value`.
 *
 * Instead of a single generic YAML/JSON editor, this renders the control that
 * matches the attribute's chosen `datatype` (a boolean switch, a number input, a
 * date/time picker, a dropdown of the attribute's allowed values, a single-line
 * text field, or a multiline Monaco codeEditor for TEXT/MARKDOWN/JSON shapes —
 * never a plain <textarea>). It reads the sibling `datatype` / `allowed_values`
 * fields from the enclosing form via `useFormState`, so it re-renders the right
 * control as the datatype is chosen. A trash-icon button clears the field to null
 * ("no default"), which is distinct from an empty-string default.
 *
 * The form field value is the *stored* structured value (string / number /
 * boolean / object / null), so no datatype-aware (de)serialisation is needed in
 * the resource config's submitDto/initialValueDTO — this component owns it. An
 * empty control means "no default" and maps to `null`. For a reference datatype
 * (RECORD_REFERENCE / OBJECT_TEMPLATE_REFERENCE) it renders a searchable record
 * dropdown scoped to the referenced type/template and stores the picked record id;
 * the backend validates that the target exists and matches the reference
 * constraints.
 */
export const DefaultValueEditor = ({ input, fieldProps, fieldAttr, meta }: FormComponentProps) => {
    const { values } = useFormState({ subscription: { values: true } });
    const datatype: string | undefined = values?.datatype;
    const kind = controlKindForDatatype(datatype);

    const isReadOnly = Boolean(fieldProps?.isReadOnly || fieldAttr?.disabled);
    const label = fieldProps?.label ?? "Default value";
    const hasError = Boolean((fieldProps?.validateOnMount || meta?.touched) && meta?.error);
    const value = input.value;
    // react-final-form renders an absent/null field value as "", so treat null,
    // undefined and blank uniformly as "no default" on the read side (a real
    // boolean `false` / number `0` is not blank and stays shown).
    const isUnset = isUnsetDefaultValue(value);

    // Local draft for the raw YAML/JSON editor so typing an intermediate,
    // not-yet-valid value doesn't reformat the text on every keystroke. Reset
    // whenever the datatype changes (e.g. the user switches to/from JSON).
    const [jsonDraft, setJsonDraft] = useState<string>(() => serializeJsonDraft(value));
    useEffect(() => {
        setJsonDraft(serializeJsonDraft(input.value));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datatype]);

    const commit = (next: unknown) => {
        input.onChange(next);
        input.onBlur?.();
    };

    // Reference-datatype default: a record picker scoped to the referenced type.
    // - RECORD_REFERENCE          -> records of `reference_record_type` (the enum
    //   value lowercased is the graphql resource name, e.g. FLOW -> "flow").
    // - OBJECT_TEMPLATE_REFERENCE -> custom_objects filtered to the referenced
    //   `reference_object_template_id` (server-side queryField on object_template_id).
    // Both sibling fields are read from the enclosing form via useFormState, so the
    // picker re-scopes as they are chosen. Memoised so a fresh options object
    // doesn't churn the async-select loader on every keystroke elsewhere in the form.
    const referenceRecordType: string | undefined = values?.reference_record_type;
    const referenceObjectTemplateId: string | undefined = values?.reference_object_template_id;
    const isObjectTemplateReference = datatype === "OBJECT_TEMPLATE_REFERENCE";
    const referenceResource = useMemo<string | undefined>(() => {
        if (kind !== "reference") return undefined;
        if (isObjectTemplateReference) return "custom_object";
        return referenceRecordType ? String(referenceRecordType).toLowerCase() : undefined;
    }, [kind, isObjectTemplateReference, referenceRecordType]);
    const referenceOptions = useMemo<AutocompleteOptionsProps | undefined>(() => {
        if (!referenceResource) return undefined;
        // OBJECT_TEMPLATE_REFERENCE needs a chosen template to scope the CO list.
        if (isObjectTemplateReference && !referenceObjectTemplateId) return undefined;
        return {
            resource: referenceResource,
            ...(isObjectTemplateReference
                ? {
                      queryField: {
                          field: "object_template_id",
                          op: "eq",
                          value: referenceObjectTemplateId,
                      },
                  }
                : {}),
        };
    }, [referenceResource, isObjectTemplateReference, referenceObjectTemplateId]);

    // A trash-icon "clear default value" button that resets the field to null
    // ("no default"). Mirrors the UX & visual design of the schema-form
    // form-optional-* clear/remove control (IconCollection.deleteOutline, white
    // variation, wrapped in a Tooltip). An empty string default and "no default"
    // are semantically different, so clearing must be an explicit action rather
    // than the ambiguous "leave empty" affordance. Shown whenever a value is set
    // (setting a default to null — "no default" — is a valid attribute config
    // regardless of is_nullable).
    const clearButton = () =>
        !isReadOnly && !isUnset ? (
            <Tooltip overlay="Clear default value (no default)">
                <span className="defaultValueEditor__clear">
                    <Button
                        icon={IconCollection.deleteOutline}
                        variation="white"
                        onClick={() => commit(null)}
                        labelOnlyAria
                        label="Clear default value"
                    />
                </span>
            </Tooltip>
        ) : null;

    const renderControl = () => {
        switch (kind) {
            case "reference": {
                // The referenced type/template must be chosen first (the picker
                // has nothing to scope to otherwise).
                if (!referenceOptions) {
                    return (
                        <div className="defaultValueEditor__note defaultValueEditor__note--muted">
                            {isObjectTemplateReference
                                ? "Choose the referenced object template first, then pick a default record."
                                : "Choose the referenced record type first, then pick a default record."}
                        </div>
                    );
                }
                return (
                    <div className="defaultValueEditor__reference">
                        <Autocomplete
                            name={input.name}
                            options={referenceOptions}
                            value={value == null ? "" : value}
                            onChange={(next: any) =>
                                commit(next == null || next === "" ? null : next)
                            }
                            onBlur={() => input.onBlur?.()}
                            isDisabled={isReadOnly}
                            additionalProps={{ fieldWidth: "100%" }}
                        />
                    </div>
                );
            }

            case "boolean": {
                const boolValue = value === true;
                return (
                    <div className="defaultValueEditor__inline">
                        <SimpleToggle
                            identifier={input.name}
                            value={boolValue}
                            isReadOnly={isReadOnly}
                            additionalAttributes={{
                                onChange: (checked: boolean) => commit(checked),
                            }}
                        />
                        <span className="defaultValueEditor__stateLabel">
                            {isUnset ? "no default" : boolValue ? "true" : "false"}
                        </span>
                    </div>
                );
            }

            case "number":
                return (
                    <NumberInput
                        name={input.name}
                        inputProps={{
                            value: value == null ? ("" as any) : value,
                            isReadOnly,
                            onChange: (event: ChangeEvent<HTMLInputElement>) => {
                                const raw = event.target.value;
                                if (raw === "") return commit(null);
                                const parsed = Number(raw);
                                return commit(Number.isNaN(parsed) ? raw : parsed);
                            },
                        }}
                        attributes={{ placeholder: "no default" }}
                    />
                );

            case "text":
                return (
                    <Text
                        name={input.name}
                        inputProps={{
                            value: value == null ? "" : String(value),
                            isReadOnly,
                            onChange: (event: ChangeEvent<HTMLInputElement>) =>
                                commit(event.target.value === "" ? null : event.target.value),
                        }}
                        attributes={{ placeholder: "no default" }}
                    />
                );

            case "textarea": {
                // TEXT / MARKDOWN: multiline plain string. Always a codeEditor,
                // never a <textarea> (Stefan's standing rule). MARKDOWN gets
                // markdown highlighting; TEXT stays plaintext. Empty editor ->
                // null ("no default").
                const lang: FormComponentPropLanguages =
                    datatype === "MARKDOWN" ? "markdown" : "text";
                return (
                    <LazyResizeableCodeEditor
                        input={{
                            ...input,
                            value: value == null ? "" : String(value),
                            onChange: (next: string) =>
                                commit(next === "" ? null : next),
                        }}
                        meta={{}}
                        fieldAttr={{ ...fieldAttr, disabled: isReadOnly }}
                        fieldProps={{
                            lang,
                            label: "",
                            isReadOnly,
                            isNewRecord: true,
                            initialHeight: 140,
                            minHeight: 90,
                        }}
                    />
                );
            }

            case "date":
            case "datetime":
            case "time": {
                const format = kind === "date" ? "YYYY-MM-DD" : "HH:mm:ss";
                return (
                    <div className="defaultValueEditor__inline">
                        <GenericDateTime
                            identifier={input.name}
                            type={kind === "datetime" ? "datetime-local" : kind}
                            fieldAttr={{ ...fieldAttr, disabled: isReadOnly }}
                            input={{
                                value: typeof value === "string" ? value : "",
                                onChange: (date: any) => {
                                    if (!date) return commit(null);
                                    return commit(
                                        kind === "datetime" ? date.toISOString() : date.format(format)
                                    );
                                },
                            }}
                        />
                    </div>
                );
            }

            case "select": {
                const rows: AllowedValueRow[] = normalizeAllowedValuesRows(values?.allowed_values);
                if (rows.length === 0) {
                    return (
                        <div className="defaultValueEditor__note defaultValueEditor__note--muted">
                            Add allowed values first, then choose which one is the default.
                        </div>
                    );
                }
                return (
                    <select
                        name={input.name}
                        aria-label={input.name}
                        data-testid={input.name}
                        className="input__field"
                        disabled={isReadOnly}
                        value={value == null ? "" : String(value)}
                        onChange={(event) =>
                            commit(event.target.value === "" ? null : event.target.value)
                        }
                    >
                        <option value="">— No default —</option>
                        {rows.map((row) => (
                            <option key={row.const} value={row.const}>
                                {row.label || row.const}
                            </option>
                        ))}
                    </select>
                );
            }

            case "json":
            default:
                // JSON / BYTES / any structured shape: edited as YAML-or-JSON
                // (the raw draft is (de)serialised with parseYaml /
                // serializeToPrettyYaml). A codeEditor with "yaml" highlighting
                // fits the actual content and reuses the existing YAML lint;
                // strict json-mode would false-flag valid YAML. Empty -> null.
                return (
                    <LazyResizeableCodeEditor
                        input={{
                            ...input,
                            value: isInvalidDraft(value)
                                ? getInvalidDraftRaw(value)
                                : jsonDraft,
                            onChange: (next: string) => {
                                setJsonDraft(next);
                                commit(parseJsonDraft(next));
                            },
                        }}
                        meta={{}}
                        fieldAttr={{ ...fieldAttr, disabled: isReadOnly }}
                        fieldProps={{
                            lang: "yaml",
                            label: "",
                            isReadOnly,
                            isNewRecord: true,
                            initialHeight: 140,
                            minHeight: 90,
                        }}
                    />
                );
        }
    };

    // The field note (fieldProps.fieldNote) is rendered once by the enclosing
    // ResourceEditFields field wrapper for every field; DO NOT also render it here
    // or it shows twice (see object_template_attribute.tsx default_value fieldNote).
    return (
        <div
            className={`defaultValueEditor${hasError ? " defaultValueEditor--error" : ""}`}
            data-testid={input.name}
        >
            <div className="defaultValueEditor__label">
                <span className="defaultValueEditor__labelText">{label}</span>
                {kind !== "reference" && clearButton()}
            </div>
            <div className="defaultValueEditor__control">{renderControl()}</div>
            {hasError && <div className="defaultValueEditor__errorText">{meta.error}</div>}
        </div>
    );
};
